// QR Kiosk v2 — KV2-9 the N2 "plate card" (owner ruling D6, 2026-08-11: flavor N2 only,
// N1 "registry sheet" rejected, no flavor switch). Geometry ported from the designer
// prototype kiosk-directions.jsx `Nameplate()` lines 285-313 with flavor="plate" — slate
// header band + rivets + double inset border + photo band + mono spec rows. The prototype's
// id-link strip is deliberately NOT here (owner ruling 2026-08-13, see the guard comment at
// the foot of the card). Every hex in that prototype maps to a token; no hex literal reaches
// this file.
//
// Renders ONLY the §4 allow-list. Every row hides cleanly when its field is empty, and the
// card must still read as a plate with any subset present (acceptance criterion 4).
import React, { useState } from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import { KioskLathe } from "../shared/KioskSilhouette";
import { Nameplate } from "./nameplateModels";
import { PlateRow } from "./PlateRow";
import { getMachineTypeLabel } from "../../../shared/utils/machineUtils";
import { getReferenceLabel } from "../../../shared/utils/machineReferenceLabels";

export interface PlateCardProps {
  data: Nameplate;
}

/** PG-5 fix carried: join the non-empty parts; version alone is still a row — never silently dropped. */
function controlSystemValue(data: Nameplate): string {
  // MACH-REF-I18N-1: only the generic "Other" brand translates; real brands render as sent.
  const brand = getReferenceLabel("brand", { name: data.controlSystemBrandName, code: data.controlSystemBrandCode });
  return [brand, data.system, data.version].filter((p) => !!p && p.trim() !== "").join(" ");
}

function PhotoBand({ data, noPhotoNote }: { data: Nameplate; noPhotoNote: string }) {
  // Source order per §4: the catalog variant is used ONLY when the vetted primary is absent.
  const src = data.imageUrl || data.catalogImageDetailUrl;
  const [broken, setBroken] = useState(false);

  const bandStyle: React.CSSProperties = {
    height: 148,
    background: "var(--bg-soft)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    borderBottom: "1px solid var(--border-soft)",
    margin: "0 5px",
    overflow: "hidden",
  };

  // A broken <img> must never leave an empty band — onError falls back to the same silhouette.
  if (!src || broken) {
    return (
      <div style={bandStyle}>
        <KioskLathe w={178} h={89} />
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 8,
            lineHeight: "10px",
            color: "var(--faint)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {noPhotoNote}
        </span>
      </div>
    );
  }

  return (
    <div style={bandStyle}>
      <img
        src={src}
        alt=""
        onError={() => setBroken(true)}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </div>
  );
}

export function PlateCard({ data }: PlateCardProps) {
  const { translate } = useTranslation("public");

  const control = controlSystemValue(data);

  // Block A / block B rows, built as lists so the first VISIBLE row of each block gets no
  // hairline — otherwise hiding row 1 would leave a stray border where the block starts.
  const blockA: Array<{ key: string; label: string; value: string; sub?: string | null }> = [];
  if (data.manufacturerName)
    blockA.push({ key: "mfr", label: translate("kioskV2NameplateManufacturer"), value: data.manufacturerName });
  if (data.model)
    blockA.push({
      key: "model",
      label: translate("kioskV2NameplateDesignation"),
      value: data.model,
      sub: getMachineTypeLabel({ name: data.typeName, code: data.typeCode }) || null,
    });
  if (data.serialNumber)
    blockA.push({ key: "sn", label: translate("kioskV2NameplateSerial"), value: data.serialNumber });
  if (data.machineNo)
    blockA.push({ key: "no", label: translate("kioskV2NameplateMachineNo"), value: data.machineNo });
  if (data.productionYear != null)
    // Never a thousands separator — String(), not toLocaleString().
    blockA.push({ key: "year", label: translate("kioskV2NameplateYear"), value: String(data.productionYear) });

  // The removed id-link strip used to close the plate (6px above it, 16px below). With the
  // strip gone the LAST visible block carries the closing space itself: 12px + the row's own
  // 10px bottom padding = a 22px quiet band, which centres the bottom rivets (they sit 8-15px
  // off the edge) instead of letting them crowd the final row. Any non-last block keeps 4px.
  const blockPad = (last: boolean) => `0 5px ${last ? 12 : 4}px`;

  const blockB: Array<{ key: string; label: string; value: string }> = [];
  if (control) blockB.push({ key: "ctrl", label: translate("kioskV2NameplateControl"), value: control });
  if (data.axisConfigurationName)
    blockB.push({
      key: "axis",
      label: translate("kioskV2NameplateAxis"),
      value: getReferenceLabel("axis", { name: data.axisConfigurationName, code: data.axisConfigurationCode }),
    });
  if (data.operationTypeName)
    blockB.push({
      key: "op",
      label: translate("kioskV2NameplateOperation"),
      value: getReferenceLabel("operation", { name: data.operationTypeName, code: data.operationTypeCode }),
    });

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--tier-accent-line)",
        borderRadius: 12,
        boxShadow: "inset 0 0 0 3px var(--card), inset 0 0 0 4px var(--tier-accent-line), var(--shadow)",
        overflow: "hidden",
        flexShrink: 0,
        position: "relative",
      }}
    >
      {/* Four rivets — the detail that makes this read as the physical plate (design lines 286-288). */}
      {[
        ["8px", "8px"],
        ["8px", null],
        [null, "8px"],
        [null, null],
      ].map(([t, l], i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{
            position: "absolute",
            top: t || "auto",
            bottom: t ? "auto" : 8,
            left: l || "auto",
            right: l ? "auto" : 8,
            width: 7,
            height: 7,
            borderRadius: 999,
            background: "var(--border)",
            boxShadow: "inset 0 1px 1.5px rgba(0,0,0,0.22)",
            zIndex: 2,
          }}
        />
      ))}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 24px",
          background: "var(--tier-accent)",
        }}
      >
        {/* Explicit type instead of the shared .eyebrow class for the same reason as PlateRow:
            .eyebrow's 11.5px is the arm's-length kiosk recalibration, and the plate is read
            in the hand. 10px / 0.1em is the artboard value. */}
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            lineHeight: "12px",
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.88)",
          }}
        >
          {translate("kioskV2NameplateEyebrow")}
        </span>
        <span style={{ flex: 1 }} />
        {/* IEC 61406 is a standard designation — never translated, never localized. */}
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            lineHeight: "12px",
            letterSpacing: "0.07em",
            color: "rgba(255,255,255,0.7)",
          }}
        >
          IEC 61406
        </span>
      </div>

      <PhotoBand data={data} noPhotoNote={translate("kioskV2NameplateNoPhoto")} />

      {blockA.length > 0 && (
        <div style={{ padding: blockPad(blockB.length === 0) }}>
          {blockA.map((r, i) => (
            <PlateRow key={r.key} label={r.label} value={r.value} sub={r.sub} top={i > 0} />
          ))}
        </div>
      )}

      {blockB.length > 0 && (
        <div style={{ borderTop: blockA.length > 0 ? "1px solid var(--border)" : "none", padding: blockPad(true) }}>
          {blockB.map((r, i) => (
            <PlateRow key={r.key} label={r.label} value={r.value} top={i > 0} />
          ))}
        </div>
      )}

      {/* NO id-link display / copy affordance on the public plate — owner ruling 2026-08-13:
          the QR is the link; the page must not invite forwarding it. Do not re-add. */}
    </div>
  );
}

export default PlateCard;
